//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PersonsModel, { addUserToAclObj, sendPasswordToMobileEmailObj } from './persons.model';

/**
 * @service PersonsService
 * @description Service for managing Person entities within the Trading Community module.
 * This service handles the lifecycle of user accounts, including registration, retrieval,
 * profile management, and role-based association (e.g., Technicians reporting to Managers).
 *
 * It serves as the central hub for user data, interacting with the `/tradingcommunity/persons/` endpoint.
 *
 * **Key Responsibilities:**
 * 1. **User Management**: Creating new users (`addPerson`), generating passwords, and managing ACLs.
 * 2. **Role-Based Retrieval**: Fetching users based on their hierarchy (e.g., Technicians by Manager ID).
 * 3. **Business Association**: retrieving users associated with specific businesses (SMEs, Providers, Buyers).
 * 4. **Security**: Handling password distribution and ACL updates.
 *
 * **Data Flow:**
 * - **Registration**: `addPerson` sends registration data to `signUp`.
 * - **Retrieval**: Various `get...` methods fetch lists of `PersonsModel` based on filters like `businessID`, `reportingTo`, etc.
 * - **Context Awareness**: Some methods check `localStorage.loginPersonType` to adjust query paths, ensuring employees see data differently than other roles.
 *
 * @feature 1. Comprehensive user CRUD operations.
 * @feature 2. Hierarchy-aware data fetching (Manager -> Technician).
 * @feature 3. Integration with Access Control List (ACL) services.
 * @feature 4. Password generation and distribution utilities.
 *
 * @architecture
 * Extends base Service<PersonsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/persons/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE', affecting query path construction for business-related fetches.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PersonsService extends Service<PersonsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/persons/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/persons/');
  }

  /**
   * @method getAllPersons
   * @async
   * @description Fetch a list of all persons in the system.
   *
   * Retrieves a large dataset (pageSize=2000) to ensure comprehensive listing.
   *
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to an array of PersonsModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllPersons() {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTechnicianOnBoardFarmPersonsByBusinessIds
   * @async
   * @description Fetch persons associated with specific businesses, typically for technician onboarding views.
   *
   * Uses a PUT request to send a complex filter object in the body.
   *
   * @param {Array<String>} busienssIds - Array of business IDs to filter by.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the filtered list of persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getTechnicianOnBoardFarmPersonsByBusinessIds(busienssIds: Array<String>) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.put<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new&include=businessID`, 
      {
        "where": {
          "businessID": busienssIds
      }
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllTechniciansByProviderManagerId
   * @async
   * @description Fetch all technicians reporting to a specific manager.
   *
   * @param {string} managerId - The unique identifier of the manager.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of technicians.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllTechniciansByProviderManagerId(managerId) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}?reportingTo=${managerId}&pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addPerson
   * @async
   * @description Register a new person in the system (Sign Up).
   *
   * @param {PersonsModel} personData - The model containing user registration details.
   * @returns {Promise<PersonsModel>} Promise resolving to the created person data.
   * @throws {string} Rejects with the error message if the request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public addPerson(personData: PersonsModel) {
    return new Promise<PersonsModel>((resolve, reject) => {
      const request = this.httpClient.post<PersonsModel>(`${this.baseUrl}${this.serviceUrl}signUp`, personData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error) => {
        reject(error.error.message);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersonsbyBusinessID
   * @async
   * @description Fetch persons associated with a specific business ID.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersonsbyBusinessID(businessId: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}businessID/`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersonswithBusiness
   * @async
   * @description Fetch persons along with their business details.
   *
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons with business info.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersonswithBusiness() {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}businessDetails`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method personDetailsById
   * @async
   * @description Fetch detailed information for a specific person, including business context.
   *
   * @param {string} personId - The unique identifier of the person.
   * @returns {Promise<PersonsModel>} Promise resolving to the person details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public personDetailsById(personId: string) {
    return new Promise<PersonsModel>((resolve, reject) => {
      const request = this.httpClient.get<PersonsModel>(`${this.baseUrl}${this.serviceUrl}businessDetails/personID/${personId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPersonsByBusinessId
   * @async
   * @description Fetch all persons associated with a business ID.
   * Note: This appears to be functionally identical to `getPersonsbyBusinessID`.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllPersonsByBusinessId(businessId: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}businessID/`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPersonsFarmSitesBusinessByBusinessId
   * @async
   * @description Fetch farm sites and business details for persons associated with a business.
   *
   * **Role-Based Logic:**
   * - If `loginPersonType` is 'EMPLOYEE', the query path includes the specific `businessId`.
   * - Otherwise, it uses a generic path, relying on backend context inference.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons/sites.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllPersonsFarmSitesBusinessByBusinessId(businessId: string) {
    let queryParam = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryParam = `business/${businessId}`;
    } else {
      queryParam = `business/`;
    }
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}farmSite/${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersons
   * @async
   * @description Fetch a list of persons (limited to 1000 records).
   *
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersons() {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSMEPersonsByBusinessID
   * @async
   * @description Fetch persons categorized as SME (Small/Medium Enterprise) for a business.
   *
   * @param {string} businessID - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of SME persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getSMEPersonsByBusinessID(businessID: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}SME/businessID/${businessID}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersonsByBusinessID
   * @async
   * @description Fetch persons by business ID.
   * Note: Duplicate functionality to `getPersonsbyBusinessID`.
   *
   * @param {string} businessID - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersonsByBusinessID(businessID: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}businessID/${businessID}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProviderPersonsByBusinessID
   * @async
   * @description Fetch persons categorized as Providers for a business.
   *
   * @param {string} businessID - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of provider persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getProviderPersonsByBusinessID(businessID: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}businessID/${businessID}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getBuyerPersonsByBusinessID
   * @async
   * @description Fetch persons categorized as Buyers for a business.
   *
   * @param {string} businessID - The unique identifier of the business.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of buyer persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getBuyerPersonsByBusinessID(businessID: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}buyer/businessID/${businessID}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllFarmBuddyPersons
   * @async
   * @description Fetch all persons categorized as 'Farm Buddy'.
   *
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to the list of farm buddy persons.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getAllFarmBuddyPersons() {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}farmBuddy`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersonBusinessPersonsByBusinessID
   * @async
   * @description Fetch business-related person details.
   *
   * **Role-Based Logic:**
   * - If `loginPersonType` is 'EMPLOYEE', the query path includes the specific `businessId`.
   * - Otherwise, it uses a generic path.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @returns {Promise<PersonsModel>} Promise resolving to the person/business details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersonBusinessPersonsByBusinessID(businessId: string) {
    let queryParam = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryParam = `businessID/${businessId}`;
    } else {
      queryParam = `businessID/`;
    }
    return new Promise<PersonsModel>((resolve, reject) => {
      const request = this.httpClient.get<PersonsModel>(`${this.baseUrl}${this.serviceUrl}businessPersons/${queryParam}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPersonDetailsById
   * @async
   * @description Fetch person details by ID.
   *
   * @param {string} personId - The unique identifier of the person.
   * @returns {Promise<Array<PersonsModel>>} Promise resolving to an array containing the person details.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public getPersonDetailsById(personId: string) {
    return new Promise<Array<PersonsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonsModel>>(`${this.baseUrl}${this.serviceUrl}id/${personId}`, this.httpOptions).subscribe(response => {
        if (response) {
          let data = new Array();
            data = response;
            resolve(data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method passowrdGeneration
   * @async
   * @description Generate a new random password via the backend.
   *
   * @returns {Promise<PersonsModel>} Promise resolving to the model containing the generated password.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public passowrdGeneration() {
    return new Promise<PersonsModel>((resolve, reject) => {
      const request = this.httpClient.get<PersonsModel>(`${this.baseUrl}${this.serviceUrl}generatePassword`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addUserToAcl
   * @async
   * @description Add a user to the Access Control List (ACL).
   *
   * @param {addUserToAclObj} addUserToAclObj - Object containing user credentials and role info.
   * @returns {Promise<any>} Promise resolving to the operation result.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public addUserToAcl(addUserToAclObj: addUserToAclObj) {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.post<any>(`${this.baseUrl}/user/`, addUserToAclObj, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method sendUserPwdToMobileEmail
   * @async
   * @description Send user password to their registered mobile number or email.
   *
   * @param {sendPasswordToMobileEmailObj} sendPasswordToMobileEmailObj - Object containing contact details and template info.
   * @returns {Promise<any>} Promise resolving to the operation result.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect Adds request subscription to ongoingRequests.
   */
  public sendUserPwdToMobileEmail(sendPasswordToMobileEmailObj: sendPasswordToMobileEmailObj) {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.post<any>(`${this.baseUrl}${this.serviceUrl}sendPassword`, sendPasswordToMobileEmailObj, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears ongoingRequests array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
